'use client';

import { createContext, useContext, useState, useRef, useCallback, ReactNode } from 'react';

export type FileType = 'level-dat' | 'player-data' | null;

export interface UploadedFile {
  name: string;
  type: FileType;
  data: ArrayBuffer;
  lastModified: number;
}

interface FileContextType {
  uploadedFile: UploadedFile | null;
  setUploadedFile: (file: UploadedFile | null) => void;
  clearFile: () => void;
  usercacheData: Map<string, string> | null;
  setUsercacheData: (data: Map<string, string> | null) => void;
  hasModsDetected: boolean;
  setHasModsDetected: (hasMods: boolean) => void;
  hasSinglePlayerData: boolean;
  setHasSinglePlayerData: (hasData: boolean) => void;
  /**
   * The Level.dat Editor and the Single Player Editor edit the SAME uploaded level.dat. Each used
   * to build its download from the original upload, so downloading from one and then the other
   * silently dropped the first editor's edits. An editor records each download here, and an editor
   * that opens afterwards parses the latest download instead of the original.
   * `uploadedFile.data` stays the untouched original, for the "download backup" buttons.
   */
  recordLevelDatExport: (bytes: Uint8Array, by: LevelDatEditor) => void;
  /** What an editor should parse now: the latest download of this upload, else the upload itself. */
  currentLevelDatBytes: () => { data: ArrayBuffer; editedBy: LevelDatEditor | null };
}

export type LevelDatEditor = 'level-dat' | 'singleplayer-data';

const FileContext = createContext<FileContextType | undefined>(undefined);

export function FileProvider({ children }: { children: ReactNode }) {
  const [uploadedFile, setUploadedFileState] = useState<UploadedFile | null>(null);
  const [usercacheData, setUsercacheData] = useState<Map<string, string> | null>(null);
  const [hasModsDetected, setHasModsDetected] = useState<boolean>(false);
  const [hasSinglePlayerData, setHasSinglePlayerData] = useState<boolean>(false);
  // A ref, not state: recording a download must not re-run the exporting page's parse effect.
  const latestExport = useRef<{ data: ArrayBuffer; by: LevelDatEditor } | null>(null);

  const setUploadedFile = useCallback((file: UploadedFile | null) => {
    latestExport.current = null;
    setUploadedFileState(file);
  }, []);

  const recordLevelDatExport = useCallback((bytes: Uint8Array, by: LevelDatEditor) => {
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(bytes);
    latestExport.current = { data: copy.buffer, by };
  }, []);

  const currentLevelDatBytes = useCallback(() => {
    if (latestExport.current) return { data: latestExport.current.data, editedBy: latestExport.current.by };
    return { data: uploadedFile?.data ?? new ArrayBuffer(0), editedBy: null };
  }, [uploadedFile]);

  const clearFile = () => {
    setUploadedFile(null);
    setHasModsDetected(false);
    setHasSinglePlayerData(false);
  };

  return (
    <FileContext.Provider value={{
      uploadedFile,
      setUploadedFile,
      clearFile,
      usercacheData,
      setUsercacheData,
      hasModsDetected,
      setHasModsDetected,
      hasSinglePlayerData,
      setHasSinglePlayerData,
      recordLevelDatExport,
      currentLevelDatBytes,
    }}>
      {children}
    </FileContext.Provider>
  );
}

export function useFile() {
  const context = useContext(FileContext);
  if (context === undefined) {
    throw new Error('useFile must be used within a FileProvider');
  }
  return context;
}
